import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Link from 'next/link'

const CONTACT_EMAIL = 'onare@onadev.net'
const LAST_UPDATED = 'April 16, 2025'

const Section = ({ title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant='h6' component='h2' color='primary' gutterBottom>
      {title}
    </Typography>
    {children}
  </Box>
)

const Privacy = () => {
  return (
    <>

      <Grid container spacing={6}>
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              padding: '2rem',
              borderRadius: '12px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              borderTop: '6px solid',
              borderTopColor: 'primary.main'
            }}
          >
            <Typography variant='h4' component='h1' color='primary' gutterBottom>
              Privacy Policy
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Last updated: {LAST_UPDATED}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ maxWidth: 800 }}>
              <Typography variant='body1' paragraph>
                This Privacy Policy explains how WAO Tools ("we," "us," or "our"), operated by oNare via{' '}
                <a href='https://onadev.net' target='_blank' rel='noopener noreferrer'>
                  onaDev
                </a>
                , collects, uses, and protects information when you visit waotools.com (the "Site"). By using the Site,
                you agree to the practices described here.
              </Typography>

              <Section title='1. Information We Collect'>
                <Typography variant='body1' paragraph>
                  <strong>Information you provide:</strong> If you use our contact form, we collect your name, email
                  address, and the content of your message. This information is used solely to respond to your inquiry
                  and is not stored in any database on our servers.
                </Typography>
                <Typography variant='body1' paragraph>
                  <strong>Local storage data:</strong> Many WAO Tools calculators save your inputs (troop formations,
                  artifact configurations, etc.) in your browser's local storage. This data never leaves your device
                  and is not transmitted to our servers.
                </Typography>
                <Typography variant='body1' paragraph>
                  <strong>Automatically collected information:</strong> When you visit the Site, standard web
                  technologies may automatically collect certain information, such as your IP address, browser type,
                  operating system, referring URLs, and pages visited. This data is collected via third-party analytics
                  services described below.
                </Typography>
              </Section>

              <Section title='2. Cookies and Similar Technologies'>
                <Typography variant='body1' paragraph>
                  We use cookies and similar tracking technologies. These fall into the following categories:
                </Typography>
                <Typography variant='body1' component='div'>
                  <ul>
                    <li>
                      <strong>Analytics cookies:</strong> We use Firebase Analytics and Microsoft Clarity to
                      understand how visitors interact with the Site. These services may set cookies to track
                      sessions and page views.
                    </li>
                    <li>
                      <strong>Advertising cookies:</strong> We use Google AdSense to display advertisements. Google
                      AdSense and its partners may use cookies to serve ads based on your prior visits to this site
                      or other sites on the Internet. The DoubleClick DART cookie is used by Google in the ads
                      served on this Site to serve ads to users based on their visit to this site and other sites on
                      the Internet.
                    </li>
                    <li>
                      <strong>Preference cookies:</strong> The Site stores your theme preference (light/dark mode)
                      in local storage to preserve your display settings between sessions.
                    </li>
                  </ul>
                </Typography>
                <Typography variant='body1' paragraph>
                  You may opt out of the use of the DART cookie by visiting the{' '}
                  <a
                    href='https://policies.google.com/technologies/ads'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Google Ad and Content Network Privacy Policy
                  </a>
                  .
                </Typography>
              </Section>

              <Section title='3. Google AdSense and Third-Party Advertising'>
                <Typography variant='body1' paragraph>
                  We use Google AdSense to serve advertisements on this Site. Google, as a third-party vendor, uses
                  cookies to serve ads based on a user's prior visits to waotools.com and other websites. Google's use
                  of advertising cookies enables it and its partners to serve ads based on your visit to our Site
                  and/or other sites on the Internet.
                </Typography>
                <Typography variant='body1' paragraph>
                  Third-party vendors, including Google, use cookies to serve ads based on a user's prior visits to
                  our website or to other websites. Users may opt out of personalized advertising by visiting{' '}
                  <a href='https://www.google.com/settings/ads' target='_blank' rel='noopener noreferrer'>
                    Ads Settings
                  </a>{' '}
                  or by visiting{' '}
                  <a href='https://www.aboutads.info' target='_blank' rel='noopener noreferrer'>
                    aboutads.info
                  </a>
                  . For more information on how Google uses data collected via AdSense, see{' '}
                  <a
                    href='https://policies.google.com/technologies/partner-sites'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Google's partner data policies
                  </a>
                  .
                </Typography>
              </Section>

              <Section title='4. How We Use Your Information'>
                <Typography variant='body1' component='div'>
                  <ul>
                    <li>To respond to contact form submissions</li>
                    <li>To analyze Site traffic and improve tools and content</li>
                    <li>To serve relevant advertisements via Google AdSense</li>
                    <li>To detect and prevent technical issues</li>
                  </ul>
                </Typography>
                <Typography variant='body1' paragraph>
                  We do not sell, trade, or rent your personal information to third parties.
                </Typography>
              </Section>

              <Section title='5. Data Retention'>
                <Typography variant='body1' paragraph>
                  Contact form messages are retained only as long as necessary to resolve your inquiry, typically no
                  longer than 90 days. Analytics data retained by Google Firebase and Microsoft Clarity is subject to
                  those services' own retention policies. Tool data stored in your browser's local storage persists
                  until you clear it through your browser settings.
                </Typography>
              </Section>

              <Section title='6. Your Rights'>
                <Typography variant='body1' paragraph>
                  You have the right to access, correct, or request deletion of any personal data we hold about you.
                  To exercise these rights, contact us at{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. You may also opt out of analytics tracking
                  by using browser settings, privacy extensions, or your browser's built-in "Do Not Track" setting.
                </Typography>
              </Section>

              <Section title="7. Children's Privacy">
                <Typography variant='body1' paragraph>
                  This Site is not directed at children under the age of 13. We do not knowingly collect personal
                  information from children under 13. If you believe we have inadvertently collected such information,
                  please contact us immediately at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                </Typography>
              </Section>

              <Section title='8. International Data Transfers'>
                <Typography variant='body1' paragraph>
                  WAO Tools is operated from the Dominican Republic. If you are accessing the Site from outside the
                  Dominican Republic, please be aware that your information may be transferred to, stored, and
                  processed in the Dominican Republic and in servers operated by our third-party service providers
                  (Google, Microsoft) in various countries. By using this Site, you consent to this transfer.
                </Typography>
              </Section>

              <Section title='9. Third-Party Links'>
                <Typography variant='body1' paragraph>
                  The Site may contain links to third-party websites. We are not responsible for the privacy
                  practices or content of those sites and encourage you to review their privacy policies.
                </Typography>
              </Section>

              <Section title='10. Changes to This Policy'>
                <Typography variant='body1' paragraph>
                  We may update this Privacy Policy from time to time. Changes will be posted on this page with an
                  updated "Last updated" date. Continued use of the Site after changes are posted constitutes your
                  acceptance of the revised policy.
                </Typography>
              </Section>

              <Section title='11. Contact'>
                <Typography variant='body1' paragraph>
                  For questions about this Privacy Policy, contact us at:{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                </Typography>
              </Section>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </>
  )
}

Privacy.noLoadingGate = true

export default Privacy

